import { useNavigate } from "react-router-dom";
import { LifeBuoy, Mail, MessageCircle, BookOpen, ArrowLeft } from "lucide-react";

/**
 * Public support center. Linked from the dashboard footer + landing page.
 * Lightweight static content for now — real ticketing can be wired in
 * later via a 3rd-party (Zendesk, Crisp) without changing this route.
 */
const TOPICS = [
  { q: "How do I create a workspace?", a: "Click the workspace switcher (top-left of the calendar) → New workspace." },
  { q: "Why don't I see triggers / actions?", a: "Triggers and actions are part of the Standard plan and above. Upgrade from Settings → Billing." },
  { q: "How do I share a document?", a: "Open the document, click Share. Choose Public, Invite, or Secured (password-protected)." },
  { q: "How do I cancel my subscription?", a: "Settings → Billing → Manage. You'll be returned to the Free plan immediately; data is preserved." },
  { q: "I lost work in a document.", a: "Documents auto-save every 5 seconds and on tab/window close. Use Settings → History to roll back." },
];

const CHANNELS = [
  { icon: MessageCircle, label: "Live chat", desc: "Mon–Fri 9am–5pm ET", action: "Start chat", href: "mailto:support@crescendo.dev" },
  { icon: Mail, label: "Email", desc: "We reply within 1 business day", action: "support@crescendo.dev", href: "mailto:support@crescendo.dev" },
  { icon: BookOpen, label: "Docs", desc: "Long-form guides and API reference", action: "Browse docs", href: "/docs" },
];

export default function SupportCenter() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#EDEDED] p-8" data-testid="support-center-page">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => nav(-1)}
          className="text-xs text-[#A1A1AA] hover:text-[#EDEDED] mb-6 inline-flex items-center gap-1"
          data-testid="support-back-btn"
        >
          <ArrowLeft className="w-3 h-3" /> Back
        </button>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-[#5A90F5]/15 flex items-center justify-center">
            <LifeBuoy className="w-5 h-5 text-[#5A90F5]" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-semibold">Support Center</h1>
            <p className="text-sm text-[#A1A1AA]">We've got your back. Search common questions or reach out to the team.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
          {CHANNELS.map((c, i) => (
            <a
              key={i}
              href={c.href}
              className="rounded-lg border border-[#2A2A2A] bg-[#141414] p-4 hover:border-[#5A90F5] transition-colors block"
              data-testid={`support-channel-${i}`}
            >
              <c.icon className="w-5 h-5 text-[#5A90F5] mb-2" />
              <div className="font-display font-semibold text-sm">{c.label}</div>
              <div className="text-xs text-[#A1A1AA] mt-1">{c.desc}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#5A90F5] mt-3">{c.action} →</div>
            </a>
          ))}
        </div>

        <h2 className="font-display text-lg font-semibold mb-3">Common questions</h2>
        <div className="space-y-2" data-testid="support-faq">
          {TOPICS.map((t, i) => (
            <details
              key={i}
              className="rounded-md border border-[#2A2A2A] bg-[#141414] p-3 group"
              data-testid={`support-faq-${i}`}
            >
              <summary className="cursor-pointer text-sm font-medium list-none flex items-center justify-between">
                {t.q}
                <span className="text-[#A1A1AA] text-xs group-open:rotate-180 transition-transform">▾</span>
              </summary>
              <p className="text-xs text-[#A1A1AA] mt-2 leading-relaxed">{t.a}</p>
            </details>
          ))}
        </div>

        <p className="text-[10px] text-[#52525B] text-center mt-8">
          Can't find what you need? Email <a href="mailto:support@crescendo.dev" className="text-[#5A90F5] hover:underline">support@crescendo.dev</a>.
        </p>
      </div>
    </div>
  );
}
